import React, { ChangeEvent, FormEvent, MouseEvent, useEffect, useMemo, useState } from 'react';

import {
  Button,
  ExcelIcon,
  Form,
  Input,
  Pagination,
  Radio,
  RadioGroup,
  RefreshIcon,
  Select,
  Text,
  TextButton,
  TypeSelectOption,
  toastService,
} from '@thefarmersfront/kitchen';

import { PageTitle, Table, TableTopField } from '~/components/common';
import { PageWrapper } from '~/components/layout';

import { useDownloadReady, useStableCallback, useToggle } from '~/hooks/common';
import { useGetStocksQueryType, usePostStocksSku, usePostStocksSkuExcelDownload } from '~/hooks/stock';
import { useLoadingStore } from '~/store';
import { transformToStockSelectOptions as transformToSelectOptions } from '~/utils';
import { getStockSkuColumnDefs } from '~/utils/stock';

import { BREAD_CRUMB, PATH, TOAST_MESSAGE } from '~/constants';
import {
  INITIAL_STOCK_PAGE_OFFSET,
  INITIAL_STOCK_PAGE_SIZE,
  STOCK_PAGE_SIZE,
  TypeStockPageSize,
} from '~/constants/stock';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypePostStocksSkuParams, TypeStockSku, TypeStockSkuFilter } from '~/types/stock.types';

import styles from './StockSkuPage.module.scss';

const INITIAL_STOCK_SKU_FILTER: TypeStockSkuFilter = {
  keyword: { value: '', text: '' },
  keywordValue: '',
  center: { value: '', text: '' },
  temperature: { value: '', text: '' },
  isZeroStock: false,
  pageSize: INITIAL_STOCK_PAGE_SIZE,
  currentPage: INITIAL_STOCK_PAGE_OFFSET,
};

const StockSkuPage = () => {
  const { setIsLoading } = useLoadingStore();

  const [stockSkuFilter, setStockSkuFilter] = useState<TypeStockSkuFilter>(INITIAL_STOCK_SKU_FILTER);

  const [stockSkuData, setStockSkuData] = useState<TypeStockSku[] | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const { getStocksQueryType } = useGetStocksQueryType();
  const { postStocksSku } = usePostStocksSku();
  const { postStocksSkuExcelDownload } = usePostStocksSkuExcelDownload();

  const [stockKeyword, setStockKeyword] = useState<TypeSelectOption<string>[]>([]);
  const [stockCenter, setStockCenter] = useState<TypeSelectOption<string>[]>([]);
  const [stockTemperature, setStockTemperature] = useState<TypeSelectOption<string>[]>([]);

  const pageOptions = Object.values(STOCK_PAGE_SIZE);

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const { isDownloadReady, searchedFilter, markDownloadReady, checkFilterChange } = useDownloadReady({
    initialFilter: INITIAL_STOCK_SKU_FILTER,
  });

  const { columnDefs } = useMemo(() => getStockSkuColumnDefs(), []);

  const fetchStocksQueryType = useStableCallback(async () => {
    try {
      setIsLoading(true);

      const { data } = await getStocksQueryType();

      const { keyWord, thirdPartyClusterType, temperatureType } = data;

      const keywordOptions = transformToSelectOptions(keyWord);
      const centerOptions = transformToSelectOptions(thirdPartyClusterType);
      const temperatureOptions = transformToSelectOptions(temperatureType);

      setStockKeyword(keywordOptions);
      setStockCenter(centerOptions);
      setStockTemperature(temperatureOptions);

      const initialStockSkuFilter: TypeStockSkuFilter = {
        ...stockSkuFilter,
        keyword: keywordOptions?.[0],
        center: centerOptions?.[0],
        temperature: temperatureOptions?.[0],
      };

      setStockSkuFilter(initialStockSkuFilter);
      markDownloadReady({ filterValue: initialStockSkuFilter, hasData: false });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        persist: true,
        message:
          (error as TypeErrorResponseData['error'])?.message ||
          '재고 필터 정보를 호출하는데 실패했습니다.\n페이지를 새로고침 해주세요.',
      });
    } finally {
      setIsLoading(false);
    }
  });

  const fetchStockSku = async (stockSkuFilter: TypeStockSkuFilter) => {
    try {
      setIsLoading(true);

      const { keyword, keywordValue, center, temperature, isZeroStock, pageSize, currentPage } = stockSkuFilter;

      const params: TypePostStocksSkuParams = {
        keyWord: String(keyword.value),
        keyWordValue: keywordValue?.trim(),
        thirdPartyClusterType: String(center.value),
        temperatureType: String(temperature.value),
        isZeroStock,
        limit: pageSize,
        cursor: currentPage,
      };

      const { data, pagination } = await postStocksSku(params);

      setStockSkuFilter(stockSkuFilter);
      setStockSkuData(data);
      setTotalCount(pagination.listTotal);

      markDownloadReady({ filterValue: stockSkuFilter, hasData: data.length > 0 });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message:
          (error as TypeErrorResponseData['error'])?.message ||
          '재고 정보를 호출하는데 실패했습니다.\n다시 시도해주세요.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStockSkuExcelDownload = async () => {
    const downloadingToastId = toastService.addToast({ message: TOAST_MESSAGE.DOWNLOADING, persist: true });

    try {
      const params = {
        keyWord: searchedFilter.keyword.value,
        keyWordValue: searchedFilter.keywordValue,
        thirdPartyClusterType: searchedFilter.center.value,
        temperatureType: searchedFilter.temperature.value,
        isZeroStock: searchedFilter.isZeroStock,
      };

      await postStocksSkuExcelDownload(params);

      toastService.addToast({
        message: TOAST_MESSAGE.DOWNLOAD_SUCCESS,
      });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      toastService.removeToast(downloadingToastId);
    }
  };

  const handleSubmitSearch = (e: FormEvent) => {
    e.preventDefault();

    fetchStockSku({
      ...stockSkuFilter,
      currentPage: INITIAL_STOCK_PAGE_OFFSET,
    });
  };

  const handleClickReset = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    setStockSkuFilter({
      ...stockSkuFilter,
      keyword: stockKeyword?.[0],
      keywordValue: '',
      center: stockCenter?.[0],
      temperature: stockTemperature?.[0],
      isZeroStock: false,
    });
  };

  const handleClickExcelDownload = () => {
    fetchStockSkuExcelDownload();
  };

  const handleChangeKeyword = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setStockSkuFilter({
      ...stockSkuFilter,
      keyword: selectedOption,
      keywordValue: '',
    });
  };

  const handleChangeKeywordValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setStockSkuFilter({ ...stockSkuFilter, keywordValue: value });
  };

  const handleBlurKeywordValue = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setStockSkuFilter({ ...stockSkuFilter, keywordValue: value.trim() });
  };

  const handleChangeCenter = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setStockSkuFilter({
      ...stockSkuFilter,
      center: selectedOption,
    });
  };

  const handleChangeTemperatureType = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setStockSkuFilter({
      ...stockSkuFilter,
      temperature: selectedOption,
    });
  };

  const handleChangeIsZeroStock = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setStockSkuFilter({
      ...stockSkuFilter,
      isZeroStock: value === 'true',
    });
  };

  const handleChangeLimit = (_: unknown, { value }: TypeSelectOption) => {
    fetchStockSku({
      ...stockSkuFilter,
      pageSize: value as TypeStockPageSize,
      currentPage: 0,
    });
  };
  const handleClickPage = (e: MouseEvent<HTMLButtonElement>) => {
    const newPage = Number(e.currentTarget.value);

    fetchStockSku({
      ...stockSkuFilter,
      currentPage: newPage - 1,
    });
  };

  const handleFilterChange = useStableCallback(() => checkFilterChange(stockSkuFilter));

  useEffect(() => {
    fetchStocksQueryType();
  }, [fetchStocksQueryType]);

  useEffect(() => {
    handleFilterChange();
  }, [stockSkuFilter, handleFilterChange]);

  return (
    <PageWrapper>
      <PageTitle
        title="상품별 조회"
        breadcrumbs={BREAD_CRUMB[PATH.STOCK_SKU]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <Form className={styles['filter-section']} onSubmit={handleSubmitSearch}>
          <div className={styles['filters-container']}>
            <div className={styles['filter-container']}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                키워드
              </Text>
              <div className={styles['input-container']}>
                <Select
                  className={styles['input-select']}
                  placeholder="선택"
                  value={stockSkuFilter.keyword}
                  options={stockKeyword}
                  onChange={handleChangeKeyword}
                  size="small"
                />
                <Input
                  type="text"
                  value={stockSkuFilter.keywordValue}
                  placeholder="입력"
                  size="small"
                  maxLength={200}
                  onChange={handleChangeKeywordValue}
                  onBlur={handleBlurKeywordValue}
                />
              </div>
            </div>
            <div className={`${styles['filter-column-container']} ${styles['filter-column-container-span-2']}`}>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  보관 센터
                </Text>
                <Select
                  className={styles['center-select']}
                  placeholder="선택"
                  value={stockSkuFilter.center}
                  options={stockCenter}
                  size="small"
                  onChange={handleChangeCenter}
                />
              </div>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  보관 유형
                </Text>
                <Select
                  className={styles['center-select']}
                  placeholder="선택"
                  value={stockSkuFilter.temperature}
                  options={stockTemperature}
                  size="small"
                  onChange={handleChangeTemperatureType}
                />
              </div>
            </div>
            <div className={`${styles['filter-column-container']} ${styles['filter-column-container-span-2']}`}>
              <div className={`${styles['filter-container']} ${styles['radio-filter-container']}`}>
                <Text
                  className={`${styles['filter-label']} ${styles['radio-filter-label']}`}
                  size="small"
                  weight="semi-bold"
                >
                  조회 대상 상품
                </Text>
                <RadioGroup
                  className={styles['zero-stock-radio-group']}
                  name="zero-stock-radio-group"
                  size="medium"
                  value={stockSkuFilter.isZeroStock ? 'true' : 'false'}
                  onChange={handleChangeIsZeroStock}
                >
                  <Radio label="재고 보유" value={'false'} />
                  <Radio label="품절" value={'true'} />
                </RadioGroup>
              </div>
            </div>
          </div>
          <div className={styles['filter-actions']}>
            <div className={styles['filter-actions-left-field']} />
            <div className={styles['filter-actions-center-field']}>
              <Button type="submit" size="small">
                검색
              </Button>
            </div>
            <div className={styles['filter-actions-right-field']}>
              <TextButton
                className={styles['filter-reset-button']}
                leftIcon={<RefreshIcon size={16} />}
                size="small"
                color="secondary"
                onClick={handleClickReset}
              >
                필터 초기화
              </TextButton>
            </div>
          </div>
        </Form>
      )}

      <div className={styles['view-section']}>
        <TableTopField
          dataTotalCount={totalCount ?? 0}
          rightButton={
            <Button
              variant="outlined"
              color="tertiary"
              size="tiny"
              leftIcon={<ExcelIcon size={16} />}
              disabled={!isDownloadReady}
              onClick={handleClickExcelDownload}
            >
              엑셀 다운로드
            </Button>
          }
        />
        <div className={styles['view-section-table-wrapper']}>
          <Table
            className={styles['view-table']}
            data={stockSkuData}
            columns={columnDefs}
            rowStatusType={stockSkuData === null ? 'INIT' : 'EMPTY'}
          />
        </div>
      </div>
      <div className={styles['view-actions-section']}>
        <div className={styles['view-options']}>
          <Select
            className={styles['limit-select']}
            options={pageOptions}
            value={STOCK_PAGE_SIZE[stockSkuFilter.pageSize]}
            size="tiny"
            dropdownPlacement="top-left"
            onChange={handleChangeLimit}
          />
        </div>
        {totalCount ? (
          <Pagination
            className={styles['pagination']}
            currentPage={stockSkuFilter.currentPage + 1}
            totalCount={totalCount ?? 0}
            pageSize={stockSkuFilter.pageSize}
            onClick={handleClickPage}
          />
        ) : null}
      </div>
    </PageWrapper>
  );
};

export default StockSkuPage;
